/**
 * What a user data change actually did.
 *
 * The consequences of a change are rarely only the thing clicked: voiding a run can take an
 * achievement, which takes points, which can drop a rank; revoking a collection achievement can
 * cost the collection its badge. The server reports each consequence as a sentence, and the admin
 * is shown every one of them rather than left to work it out from the lists refreshing.
 */

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import type { AdminUserDataChangeResultDto } from '@api/types/userData'

export interface UserDataChangeResultDialogProps {
  open: boolean
  result: AdminUserDataChangeResultDto | null
  onClose: () => void
}

export function UserDataChangeResultDialog({ open, result, onClose }: UserDataChangeResultDialogProps) {
  const mobileDialogProps = useMobileDialogProps()

  const delta = result ? result.pointsAfter - result.pointsBefore : 0
  const pointsText = !result
    ? ''
    : delta === 0
      ? `Points unchanged at ${result.pointsAfter.toLocaleString()}`
      : `Points ${result.pointsBefore.toLocaleString()} → ${result.pointsAfter.toLocaleString()} (${delta > 0 ? '+' : '−'}${Math.abs(delta).toLocaleString()})`

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Change saved</DialogTitle>
      <DialogContent>
        {result && (
          <>
            <Alert severity={delta < 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              {pointsText}
            </Alert>

            {result.changes.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing else changed.
              </Typography>
            ) : (
              <List dense disablePadding>
                {result.changes.map((change, i) => (
                  // Sentences can repeat ("Lost badge: …" for two collections of the same name),
                  // so the index is part of the key.
                  <ListItem key={`${i}-${change}`} disableGutters>
                    <ListItemText primary={change} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose} fullWidth={mobileDialogProps.fullScreen}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}
